// src/router/index.tsx
import React, { Suspense } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { RequireRole } from "./guards";
import DashboardHome from "../pages/DashboardHome";
import Courses from "../pages/Courses";
import Users from "../pages/Users";
import Login from "../pages/Login";
import NotFound from "../pages/NotFound";
import Community from "../pages/Community";
import PostDetail from "../pages/PostDetail";
import Questionnaires from "../pages/Questionnaires";
import Certifications from "../pages/Certifications";
import Stats from "../pages/Stats";
import {
  ADMIN_ROLES,
  COURSES_ROLES,
  QUESTIONNAIRES_ROLES,
  CERTIFICATIONS_ROLES,
  type Role,
} from "../models/auth";
import { useAuthStore } from "../stores/authStore";
import AccessDenied from "../components/AccessDenied";
import PrivacyPolicy from "../pages/PrivacyPolicy";
import DeleteAccount from "../pages/DeleteAccount";

/** Primer destino permitido según rol (para “home” dinámico) */
function firstAllowedRoute(role: Role | null): string | null {
  if (role && (ADMIN_ROLES as readonly Role[]).includes(role)) return "/";
  switch (role) {
    case "moderator":
    case "publisher":
      return "/community";
    case "instructor":
      return "/courses";
    case "auditor":
      return "/questionnaires";
    default:
      return null;
  }
}

/** Home inteligente:
 *  - Admins: muestra DashboardHome
 *  - Otros roles: redirige a su primera sección permitida
 *  - Sin rol permitido: AccessDenied
 *  - No autenticado: a /login
 */
function HomeOrRedirect(): React.ReactElement {
  const state = useAuthStore((s) => s.state);
  const start = useAuthStore((s) => s.start);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (state.status === "idle") start();
  }, [state.status, start]);

  if (state.status === "loading") return <div className="p-6">Cargando…</div>;

  if (!state.user) {
    // Mantiene la intención de volver a donde estaba, si aplica
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const role: Role | null = state.profile?.role ?? null;

  if (role && (ADMIN_ROLES as readonly Role[]).includes(role)) {
    return <DashboardHome />; // Admin ve inicio
  }

  const fallback = firstAllowedRoute(role);
  if (fallback) return <Navigate to={fallback} replace />;

  return <AccessDenied />;
}

const basename = (
  process.env.PUBLIC_URL && process.env.PUBLIC_URL !== "/"
    ? process.env.PUBLIC_URL
    : "/"
) as string;

const router = createBrowserRouter(
  [
    // ───────────────── Inicio/Dashboard con lógica por rol
    { path: "/", element: <HomeOrRedirect /> },
    { path: "/dashboard", element: <HomeOrRedirect /> },

    // ───────────────── Estadísticas (solo admins)
    {
      path: "/estadisticas",
      element: (
        <RequireRole allow={ADMIN_ROLES}>
          <Stats />
        </RequireRole>
      ),
    },
    { path: "/stats", element: <Navigate to="/estadisticas" replace /> },

    // ───────────────── Usuarios (solo admins)
    {
      path: "/users",
      element: (
        <RequireRole allow={ADMIN_ROLES}>
          <Users />
        </RequireRole>
      ),
    },

    // ───────────────── Comunidad (moderator / publisher + admins)
    {
      path: "/community",
      element: (
        <RequireRole allow={["moderator", "publisher"] as const}>
          <Community />
        </RequireRole>
      ),
    },
    {
      path: "/community/:id",
      element: (
        <RequireRole allow={["moderator", "publisher"] as const}>
          <PostDetail />
        </RequireRole>
      ),
    },

    // ───────────────── Cursos (instructor + admins)
    {
      path: "/courses",
      element: (
        <RequireRole allow={COURSES_ROLES}>
          <Courses />
        </RequireRole>
      ),
    },

    // ───────────────── Cuestionarios (auditor + admins)
    {
      path: "/questionnaires",
      element: (
        <RequireRole allow={QUESTIONNAIRES_ROLES}>
          <Questionnaires />
        </RequireRole>
      ),
    },

    // ───────────────── Certificaciones (auditor + admins)
    {
      path: "/certifications",
      element: (
        <RequireRole allow={CERTIFICATIONS_ROLES}>
          <Certifications />
        </RequireRole>
      ),
    },

    // ───────────────── Auth / páginas públicas / 404
    { path: "/login", element: <Login /> },

    // Política de privacidad (ruta pública, sin RequireRole)
    { path: "/privacy", element: <PrivacyPolicy /> },

    // Página pública para eliminación de cuenta / datos (Delete Account URL)
    { path: "/delete-account", element: <DeleteAccount /> },

    { path: "*", element: <NotFound /> },
  ],
  { basename }
);

export default function AppRouter(): React.ReactElement {
  return (
    <Suspense fallback={<div className="p-6">Cargando…</div>}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
